import { useState, useEffect, useRef } from "react";
import React from "react";
import Icon from "../components/Icon";
import { haversineM, fmtDuration } from "../utils";
import TruckLog from "../components/TruckLog";

// ─── TIMESHEETS ───────────────────────────────────────────────────────────────
const roundDown15 = (ms) => Math.floor(ms / (15 * 60 * 1000)) * (15 * 60 * 1000);
const roundUp15   = (ms) => Math.ceil(ms  / (15 * 60 * 1000)) * (15 * 60 * 1000);
const toHM = (decimal) => {
  if (!decimal || isNaN(decimal)) return '0h';
  const h = Math.floor(decimal);
  const m = Math.round((decimal - h) * 60);
  return m === 0 ? `${h}h` : `${h}h ${String(m).padStart(2,'0')}min`;
};


function Timesheets({ t, lang, timesheets, addTimesheet, updateTimesheet, deleteTimesheet, jobs, teamMembers, role, userDoc, firebaseUser, priceList, settings }) {
  const tt = t.timesheets;

  // ── State ──
  const [activeTab, setActiveTab] = useState("pointages");
  const [selectedJobId, setSelectedJobId] = useState("");
  const [clockForId, setClockForId] = useState("self");
  const [gpsStatus, setGpsStatus] = useState("idle");
  const [gpsPos, setGpsPos] = useState(null);
  const [gpsOverride, setGpsOverride] = useState(false);
  const [nowMs, setNowMs] = useState(Date.now());
  const [actionPending, setActionPending] = useState(false);
  // Standard time modal
  const [timeModal, setTimeModal] = useState(null);
  const [timeChoice, setTimeChoice] = useState("standard");
  const [punchInNote, setPunchInNote] = useState("");
  // Post-punch-out allocation
  const [allocSession, setAllocSession] = useState(null);
  useEffect(() => {
    if (allocSession) {
      document.body.classList.add("alloc-open");
    } else {
      document.body.classList.remove("alloc-open");
    }
    return () => {
      document.body.classList.remove("alloc-open");
    };
  }, [allocSession]);
  const [taskAllocations, setTaskAllocations] = useState([]);
  const [breakTaken, setBreakTaken] = useState(false);
  const [dayNotes, setDayNotes] = useState("");
  // Admin Tab 2 filters
  const [filterEmployee, setFilterEmployee] = useState("");
  const [filterJob, setFilterJob] = useState("");
  // Sommaire
  const [summWeekStart, setSummWeekStart] = useState(() => { const d=new Date(); d.setDate(d.getDate()-d.getDay()+1); return d.toISOString().slice(0,10); });
  // Rejection flow
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [editSession, setEditSession] = useState(null);
  const [editingTimesheet, setEditingTimesheet] = useState(null);
  const [activeGuideTab, setActiveGuideTab] = useState("pointage");
  // Override punch-out
  const [overrideModal, setOverrideModal] = useState(null); // { ts, modalOpenedAt }
  const [overrideTime, setOverrideTime] = useState("");
  const [overrideError, setOverrideError] = useState("");
  // Révision tab date nav
  const [revDate, setRevDate] = useState(() => new Date().toISOString().slice(0,10));
  const [selectedTsIds, setSelectedTsIds] = useState(new Set());
  const revDateInputRef = useRef(null);
  // Feature 1 — Edit punch times (admin only)
  const [editTimesModal, setEditTimesModal] = useState(null); // { ts, modalOpenedAt }
  const [editTimesIn, setEditTimesIn] = useState("");
  const [editTimesOut, setEditTimesOut] = useState("");
  const [editTimesError, setEditTimesError] = useState("");
  // Feature 2 — Manual entry (admin only)
  const [manualEntryModal, setManualEntryModal] = useState(false);
  const [manualEntryForm, setManualEntryForm] = useState({ employeeId:"", jobId:"", date:"", punchIn:"", punchOut:"", breakTaken:false, notes:"" });
  const [manualEntryError, setManualEntryError] = useState("");
  const [manualEntryPending, setManualEntryPending] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const myId = firebaseUser?.uid || "unknown";
  const myName = userDoc?.displayName || firebaseUser?.email || "Unknown";
  const activePunchId = clockForId === "self" ? myId : clockForId;
  const activePunchName = clockForId === "self" ? myName : (teamMembers.find(m => m.id === clockForId)?.name || clockForId);
  const activeSession = timesheets.find(ts => ts.employeeId === activePunchId && !ts.punchOut);

  const parseStdTime = (timeStr) => {
    if (!timeStr) return null;
    const [h, m] = timeStr.split(":").map(Number);
    const d = new Date(); d.setHours(h, m, 0, 0);
    return d.getTime();
  };

  const checkGps = (job) => {
    if (!job?.siteLocation?.lat) { setGpsStatus("ok"); return; }
    setGpsStatus("checking");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const dist = haversineM(pos.coords.latitude, pos.coords.longitude, job.siteLocation.lat, job.siteLocation.lng);
        setGpsPos({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setGpsStatus(dist <= (job.siteLocation.radiusMetres || 300) ? "ok" : "off");
      },
      () => setGpsStatus("error"),
      { timeout: 10000, maximumAge: 60000 }
    );
  };

  const handleJobSelect = (jid) => {
    setSelectedJobId(jid);
    setGpsStatus("idle");
    setGpsOverride(false);
    if (jid) checkGps(jobs.find(j => j.id === jid));
  };

  const canPunchIn = selectedJobId && (gpsStatus === "ok" || gpsOverride || gpsStatus === "idle");

  const handlePunchInClick = () => {
    if (!canPunchIn || activeSession) return;
const rawMs = Date.now();
    const roundedMs = roundDown15(rawMs);
    const stdMs = parseStdTime(settings?.standardStartTime);
    const tol = (settings?.startTimeTolerance ?? 15) * 60 * 1000;
    const fmtT = (ms) => new Date(ms).toLocaleTimeString("fr-CA", { hour:"2-digit", minute:"2-digit" });
    if (stdMs && Math.abs(rawMs - stdMs) <= tol) {
      doPunchIn(stdMs, rawMs, true, false, "");
    } else if (stdMs) {
      setTimeChoice("standard");
      setPunchInNote("");
      setTimeModal({ rawMs, roundedMs, stdMs, stdStr: fmtT(stdMs), roundedStr: fmtT(roundedMs), rawStr: fmtT(rawMs) });
    } else {
      doPunchIn(roundedMs, rawMs, false, false, "");
    }
  };

  const doPunchIn = async (finalMs, rawMs, snapped, needsReview, note) => {
    setActionPending(true);
    setTimeModal(null);
    const job = jobs.find(j => j.id === selectedJobId);
    await addTimesheet({
      employeeId: activePunchId,
      employeeName: activePunchName,
      jobId: selectedJobId,
      jobTitle: job?.title || "",
      punchInRaw: new Date(rawMs).toISOString(),
      punchInFinal: new Date(finalMs).toISOString(),
      punchIn: new Date(finalMs).toISOString(),
      punchInLocation: gpsPos || null,
      punchInNote: note || "",
      snappedToStandard: snapped,
      needsAdminReview: needsReview,
      punchOut: null, roundedDuration: null,
      status: "draft", breakTaken: false, dayNotes: "", taskAllocations: [],
    });
    setSelectedJobId(""); setGpsStatus("idle"); setGpsOverride(false); setActionPending(false);
  };

  const confirmTimeModal = () => {
    if (!timeModal) return;
    const { rawMs, roundedMs, stdMs } = timeModal;
    if (timeChoice === "standard") doPunchIn(stdMs, rawMs, false, false, "");
    else if (timeChoice === "rounded") doPunchIn(roundedMs, rawMs, false, false, "");
    else doPunchIn(rawMs, rawMs, false, true, punchInNote);
  };

  const handlePunchOutClick = () => {
    if (!activeSession || actionPending) return;
    const rawMs = Date.now();
    const stdEndMs = parseStdTime(settings?.standardEndTime);
    const tolEnd = (settings?.endTimeTolerance ?? 15) * 60 * 1000;
    const finalOutMs = (stdEndMs && Math.abs(rawMs - stdEndMs) <= tolEnd) ? stdEndMs : roundUp15(rawMs);
    const rawDuration = finalOutMs - new Date(activeSession.punchIn).getTime();
    setAllocSession({ ...activeSession, punchOutMs: finalOutMs, rawDuration });
    setBreakTaken(false); setDayNotes("");
    const job = jobs.find(j => j.id === activeSession.jobId);
    const firstPhase = (job?.phases || [])[0];
    setTaskAllocations([{ id:`a_${Date.now()}`, phaseId: firstPhase?.id||"autre", phaseName: firstPhase?.title||"Autre", hours:0, type:"labour", equipmentId:"", equipmentName:"" }]);
  };

  const allocTotalMs = (session) => {
    if (!session) return 0;
    return roundUp15(Math.max(0, session.rawDuration - (breakTaken ? 30*60*1000 : 0)));
  };
  const allocTotalHours = (session) => allocTotalMs(session) / 3600000;
  const allocatedHours = taskAllocations.reduce((a, r) => a + (parseFloat(r.hours) || 0), 0);
  const addAllocRow = () => setTaskAllocations(p => [...p, { id:`a_${Date.now()}`, phaseId:"autre", phaseName:"Autre", hours:0, type:"labour", equipmentId:"", equipmentName:"" }]);
  const removeAllocRow = (id) => setTaskAllocations(p => p.filter(r => r.id !== id));
  const updateAllocRow = (id, field, val) => setTaskAllocations(p => p.map(r => r.id!==id ? r : {...r,[field]:val}));

  const handleEditAllocation = (ts) => {
    const punchInMs = new Date(ts.punchIn).getTime();
    const punchOutMs = new Date(ts.punchOut).getTime();
    setEditingTimesheet(ts);
    setTaskAllocations(ts.taskAllocations || []);
    setBreakTaken(ts.breakTaken || false);
    setDayNotes(ts.dayNotes || "");
    setAllocSession({
      id: ts.id,
      jobId: ts.jobId,
      punchIn: ts.punchIn,
      punchOut: ts.punchOut,
      punchInMs,
      punchOutMs,
      rawDuration: punchOutMs - punchInMs,
      isEdit: true,
    });
  };

  const handleOverrideAllocate = (ts) => {
    const punchInMs = new Date(ts.punchIn).getTime();
    const punchOutMs = new Date(ts.punchOut).getTime();
    setEditingTimesheet(ts);
    setBreakTaken(ts.breakTaken || false);
    setDayNotes(ts.dayNotes || "");
    const job = jobs.find(j => j.id === ts.jobId);
    const firstPhase = (job?.phases || [])[0];
    setTaskAllocations([{ id:`a_${Date.now()}`, phaseId: firstPhase?.id||"autre", phaseName: firstPhase?.title||"Autre", hours:0, type:"labour", equipmentId:"", equipmentName:"" }]);
    setAllocSession({
      id: ts.id,
      jobId: ts.jobId,
      punchIn: ts.punchIn,
      punchOut: ts.punchOut,
      punchInMs,
      punchOutMs,
      rawDuration: punchOutMs - punchInMs,
      isEdit: true,
      isOverrideRealloc: true,
    });
  };

  const confirmAllocation = async () => {
    if (!allocSession) return;
    setActionPending(true);
    if (allocSession.isEdit && editingTimesheet) {
      const editUpdates = {
        ...editingTimesheet,
        taskAllocations,
        breakTaken,
        dayNotes,
        editedBy: userDoc?.displayName || "Superviseur",
        editedAt: new Date().toISOString(),
      };
      if (allocSession.isOverrideRealloc) {
        editUpdates.status = "approved";
        editUpdates.approvedBy = `${myName} (${myId})`;
        editUpdates.approvedAt = new Date().toISOString();
      } else {
        editUpdates.status = "submitted";
      }
      await updateTimesheet(editingTimesheet.id, editUpdates);
      setAllocSession(null);
      setEditingTimesheet(null);
      setActionPending(false);
      return;
    }
    if (editSession) {
      const ts = editSession;
      const histEntry = {
        version: (ts.revisionHistory?.length || 0) + 1,
        submittedAt: new Date().toISOString(),
        taskAllocations,
        hours: allocTotalHours(allocSession),
        action: "submitted",
        actionBy: myName,
        actionAt: new Date().toISOString(),
        reason: "",
      };
      await updateTimesheet(ts.id, {
        ...ts,
        taskAllocations,
        breakTaken, dayNotes,
        roundedDuration: allocTotalMs(allocSession),
        status: role === "foreman" ? "submitted" : "draft",
        rejectionReason: null, rejectedBy: null, rejectedAt: null,
        revisionHistory: [...(ts.revisionHistory || []), histEntry],
      });
      setEditSession(null);
    } else {
      await updateTimesheet(allocSession.id, {
        ...allocSession,
        punchOut: new Date(allocSession.punchOutMs).toISOString(),
        roundedDuration: allocTotalMs(allocSession),
        breakTaken, dayNotes, taskAllocations,
        status: role === "foreman" ? "submitted" : "draft",
      });
    }
    setAllocSession(null); setActionPending(false);
  };

  const getEquipmentOptions = (jobId) => {
    const job = jobs.find(j => j.id === jobId);
    const fromPhases = (job?.phases || []).flatMap(p => (p.equipment||[]).map(e => ({ id:`ph_${e.name}`, name:e.name })));
    const fromPL = (priceList||[]).filter(i => i.category==="Equipment").map(i => ({ id:i.id, name:i.name }));
    const seen = new Set();
    return [...fromPhases, ...fromPL].filter(e => { if(seen.has(e.name)) return false; seen.add(e.name); return true; });
  };

  const statusStyle = (s) => ({
    draft:     { background:"#f3f4f6", color:"#6b7280" },
    submitted: { background:"#dbeafe", color:"#1d4ed8" },
    approved:  { background:"#dcfce7", color:"#166534" },
    rejected:  { background:"#fee2e2", color:"#b91c1c" },
    pending:   { background:"#fef3c7", color:"#d97706" },
  }[s] || { background:"#f3f4f6", color:"#6b7280" });

  const revisionTimesheets = timesheets.filter(ts => {
    if (filterEmployee && !ts.employeeName?.toLowerCase().includes(filterEmployee.toLowerCase())) return false;
    if (filterJob && ts.jobId !== filterJob) return false;
    return true;
  }).sort((a,b) => (b.punchIn||"").localeCompare(a.punchIn||""));

  const summWeekDays = Array.from({length:7}, (_,i) => {
    const d = new Date(summWeekStart+"T12:00:00"); d.setDate(d.getDate()+i); return d.toISOString().slice(0,10);
  });
  const summTimesheets = timesheets.filter(ts => ts.punchIn && ts.punchIn.slice(0,10)>=summWeekDays[0] && ts.punchIn.slice(0,10)<=summWeekDays[6]);
  const summHasUnapproved = summTimesheets.filter(ts=>ts.punchOut).some(ts=>ts.status!=="approved");
  const summEmployees = [...new Set(summTimesheets.map(ts=>ts.employeeName))].sort();
  const cellHours = (emp, day) => summTimesheets.filter(ts=>ts.employeeName===emp&&ts.punchIn?.slice(0,10)===day).reduce((a,ts)=>a+(ts.roundedDuration||0)/3600000,0);

  const handleOpenReject = (ts) => { setRejectModal({ ts }); setRejectReason(""); };

  const confirmReject = async () => {
    if (!rejectModal || !rejectReason.trim()) return;
    const ts = rejectModal.ts;
    setActionPending(true);
    const histEntry = {
      version: (ts.revisionHistory?.length || 0) + 1,
      submittedAt: ts.punchOut || new Date().toISOString(),
      taskAllocations: ts.taskAllocations || [],
      hours: (ts.roundedDuration || 0) / 3600000,
      action: "rejected",
      actionBy: myName,
      actionAt: new Date().toISOString(),
      reason: rejectReason.trim(),
    };
    await updateTimesheet(ts.id, {
      ...ts,
      status: "rejected",
      rejectionReason: rejectReason.trim(),
      rejectedBy: myName,
      rejectedAt: new Date().toISOString(),
      revisionHistory: [...(ts.revisionHistory || []), histEntry],
    });
    setRejectModal(null); setRejectReason(""); setActionPending(false);
  };

  const handleResubmit = (ts) => {
    const punchInMs = new Date(ts.punchIn).getTime();
    const punchOutMs = new Date(ts.punchOut).getTime();
    setEditSession(ts);
    setAllocSession({ ...ts, punchOutMs, rawDuration: punchOutMs - punchInMs });
    setBreakTaken(ts.breakTaken || false);
    setDayNotes(ts.dayNotes || "");
    setTaskAllocations((ts.taskAllocations || []).map(a => ({ ...a, id: `a_${Date.now()}_${Math.random()}` })));
  };

  const fmtTimeInput = (ms) => {
    const d = new Date(ms);
    return `${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
  };

  const handleOpenOverride = (ts) => {
    const modalOpenedAt = Date.now();
    setOverrideTime(fmtTimeInput(modalOpenedAt));
    setOverrideModal({ ts, modalOpenedAt });
    setOverrideError("");
  };

  const getOverrideTimeMs = () => {
    if (!overrideTime || !overrideModal) return null;
    const [h, m] = overrideTime.split(":").map(Number);
    const ref = new Date(overrideModal.ts.punchInFinal || overrideModal.ts.punchIn);
    const d = new Date(ref);
    d.setHours(h, m, 0, 0);
    return d.getTime();
  };

  const confirmOverride = async () => {
    if (!overrideModal) return;
    const overrideMs = getOverrideTimeMs();
    const punchInMs = new Date(overrideModal.ts.punchInFinal || overrideModal.ts.punchIn).getTime();
    if (!overrideMs || overrideMs >= overrideModal.modalOpenedAt || overrideMs <= punchInMs) return;
    // Layer 3: re-check at write time
    if (overrideMs >= Date.now()) {
      setOverrideError("Heure invalide / Invalid time");
      return;
    }
    setActionPending(true);
    const ts = overrideModal.ts;
    const breakMs = ts.breakTaken ? 30 * 60 * 1000 : 0;
    const newRoundedDuration = roundUp15(Math.max(0, overrideMs - punchInMs - breakMs));
    const overrideISO = new Date(overrideMs).toISOString();
    await updateTimesheet(ts.id, {
      ...ts,
      punchOut: overrideISO,
      punchOutFinal: overrideISO,
      punchOutRaw: overrideISO,
      roundedDuration: newRoundedDuration,
      status: "submitted",
      needsAdminReview: true,
      taskAllocations: [],
      overrideLog: {
        overriddenBy: `${myName} (${myId})`,
        overriddenAt: new Date().toISOString(),
        originalPunchOut: null,
        correctedPunchOutFinal: overrideISO,
        reason: "manual-override-punch-out",
      },
    });
    setOverrideModal(null);
    setOverrideError("");
    setActionPending(false);
  };

  // ── Edit punch times helpers (Feature 1) ──
  const getEditTimesMs = (timeStr, refDate) => {
    if (!timeStr) return null;
    const [h, m] = timeStr.split(":").map(Number);
    const d = new Date(refDate);
    d.setHours(h, m, 0, 0);
    return d.getTime();
  };

  const handleOpenEditTimes = (ts) => {
    const modalOpenedAt = Date.now();
    setEditTimesIn(fmtTimeInput(new Date(ts.punchInFinal || ts.punchIn).getTime()));
    setEditTimesOut(fmtTimeInput(new Date(ts.punchOutFinal || ts.punchOut).getTime()));
    setEditTimesModal({ ts, modalOpenedAt });
    setEditTimesError("");
  };

  const confirmEditTimes = async () => {
    if (!editTimesModal || actionPending) return;
    const ts = editTimesModal.ts;
    const refDate = new Date(ts.punchInFinal || ts.punchIn);
    const newInMs = getEditTimesMs(editTimesIn, refDate);
    const newOutMs = getEditTimesMs(editTimesOut, refDate);
    if (!newInMs || !newOutMs) return;
    if (newInMs >= newOutMs) { setEditTimesError("L'heure de départ doit être après l'heure d'entrée / Punch-out must be after punch-in"); return; }
    if (newInMs >= editTimesModal.modalOpenedAt || newOutMs >= editTimesModal.modalOpenedAt) { setEditTimesError("Les heures ne peuvent pas être dans le futur / Times cannot be in the future"); return; }
    if (newOutMs >= Date.now()) { setEditTimesError("Heure invalide / Invalid time"); return; }
    setActionPending(true);
    const breakMs = ts.breakTaken ? 30 * 60 * 1000 : 0;
    const newRoundedDuration = roundUp15(Math.max(0, newOutMs - newInMs - breakMs));
    const newInISO = new Date(newInMs).toISOString();
    const newOutISO = new Date(newOutMs).toISOString();
    await updateTimesheet(ts.id, {
      ...ts,
      punchIn: newInISO,
      punchInFinal: newInISO,
      punchOut: newOutISO,
      punchOutFinal: newOutISO,
      roundedDuration: newRoundedDuration,
      taskAllocations: [],
      status: "submitted",
      needsAdminReview: true,
      overrideLog: {
        overriddenBy: `${myName} (${myId})`,
        overriddenAt: new Date().toISOString(),
        originalPunchIn: ts.punchInFinal || ts.punchIn,
        originalPunchOut: ts.punchOutFinal || ts.punchOut,
        correctedPunchInFinal: newInISO,
        correctedPunchOutFinal: newOutISO,
        reason: "manual-time-edit",
      },
    });
    setEditTimesModal(null);
    setEditTimesError("");
    setActionPending(false);
  };

  // ── Manual entry confirm (Feature 2) ──
  const confirmManualEntry = async () => {
    const { employeeId, jobId, date, punchIn: piTime, punchOut: poTime, breakTaken: bt, notes } = manualEntryForm;
    if (!employeeId || !jobId || !date || !piTime || !poTime) return;
    const inISO = new Date(date + "T" + piTime + ":00").toISOString();
    const outISO = new Date(date + "T" + poTime + ":00").toISOString();
    const inMs = new Date(inISO).getTime();
    const outMs = new Date(outISO).getTime();
    if (outMs <= inMs) { setManualEntryError("L'heure de départ doit être après l'heure d'entrée / Punch-out must be after punch-in"); return; }
    const todayStr = new Date().toISOString().slice(0,10);
    if (date > todayStr) { setManualEntryError("La date ne peut pas être dans le futur / Date cannot be in the future"); return; }
    setManualEntryPending(true);
    const member = teamMembers.find(m => m.id === employeeId);
    const job = jobs.find(j => j.id === jobId);
    const breakMs = bt ? 30 * 60 * 1000 : 0;
    const roundedDuration = roundUp15(Math.max(0, outMs - inMs - breakMs));
    const nowISO = new Date().toISOString();
    await addTimesheet({
      employeeId,
      employeeName: member?.name || member?.displayName || employeeId,
      jobId,
      jobTitle: job?.title || "",
      date,
      punchIn: inISO,
      punchInFinal: inISO,
      punchInRaw: inISO,
      punchOut: outISO,
      punchOutFinal: outISO,
      punchOutRaw: outISO,
      roundedDuration,
      breakTaken: bt,
      dayNotes: notes,
      taskAllocations: [],
      status: "submitted",
      needsAdminReview: true,
      snapType: "manual",
      snappedToStandard: false,
      createdManually: true,
      createdBy: `${myName} (${myId})`,
      createdAt: nowISO,
      submittedAt: nowISO,
    });
    setManualEntryModal(false);
    setManualEntryForm({ employeeId:"", jobId:"", date:"", punchIn:"", punchOut:"", breakTaken:false, notes:"" });
    setManualEntryError("");
    setManualEntryPending(false);
  };

  const exportCsv = () => {
    const rows = revisionTimesheets.map(ts => [ts.employeeName, ts.jobTitle, ts.punchIn?new Date(ts.punchIn).toLocaleString("fr-CA"):"", ts.punchOut?new Date(ts.punchOut).toLocaleString("fr-CA"):"", ts.roundedDuration?(ts.roundedDuration/3600000).toFixed(2):"", ts.breakTaken?"Oui":"Non", ts.status, ts.dayNotes||""]);
    const csv = [["Employé","Chantier","Entrée","Sortie","Heures","Pause","Statut","Notes"],...rows].map(r=>r.map(c=>`"${String(c).replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob([csv],{type:"text/csv;charset=utf-8;"}); const url=URL.createObjectURL(blob);
    const a=document.createElement("a"); a.href=url; a.download=`pointage-${summWeekStart}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="page-header">
        {role==="employee"&&settings?.logoBase64&&<img src={settings.logoBase64} className="ts-header-logo" alt="logo"/>}
        <div className="page-header-row"><div><h1>{tt.title}</h1><p>{tt.subtitle}</p></div></div>
      </div>
      {role==="employee"&&(
        <div style={{display:"flex",gap:0,padding:"0 20px",borderBottom:"1px solid var(--border)",background:"var(--surface)"}}>
          {[["pointage","Pointage"],["guide","Guide"]].map(([id,label])=>(
            <button key={id} onClick={()=>setActiveGuideTab(id)} style={{padding:"10px 18px",fontSize:13,fontWeight:600,border:"none",background:"none",cursor:"pointer",borderBottom:`2px solid ${activeGuideTab===id?"var(--red)":"transparent"}`,color:activeGuideTab===id?"var(--red)":"var(--text-3)",transition:"color .15s"}}>
              {label}
            </button>
          ))}
        </div>
      )}

      {role==="employee"&&activeGuideTab==="guide"&&(
        <div className="page-body">
          <div style={{maxWidth:640,margin:"0 auto",display:"flex",flexDirection:"column",gap:20}}>

            {/* Section 1 — Arrivée */}
            {(()=>{
              const steps=[
                ["📍","Ouvrir l'onglet Pointage","Depuis l'écran principal"],
                ["📋","Sélectionner le chantier","Choisir dans la liste déroulante"],
                ["✅","Pointer l'entrée","Le GPS confirme ta position sur le site"],
              ];
              return (
                <div style={{borderRadius:10,overflow:"hidden",border:"1px solid var(--border)",background:"var(--surface)"}}>
                  <div style={{padding:"14px 18px",background:"var(--bg-2)",display:"flex",alignItems:"center",gap:12,borderBottom:"1px solid var(--border)"}}>
                    <span style={{width:28,height:28,borderRadius:"50%",background:"var(--red)",color:"#fff",fontWeight:700,fontSize:13,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>1</span>
                    <span style={{fontFamily:"var(--font-display)",fontWeight:700,fontSize:15}}>Arrivée sur le chantier</span>
                  </div>
                  {steps.map(([icon,title,desc],i)=>(
                    <div key={i} style={{display:"flex",alignItems:"center",gap:14,padding:"13px 18px",borderBottom:i<steps.length-1?"1px solid var(--border)":"none"}}>
                      <span style={{fontSize:20,flexShrink:0,width:28,textAlign:"center"}}>{icon}</span>
                      <div>
                        <div style={{fontWeight:600,fontSize:13}}>{title}</div>
                        <div style={{fontSize:12,color:"var(--text-3)",marginTop:2}}>{desc}</div>
                      </div>
                    </div>
                  ))}
                  <div style={{margin:"0 18px 14px",padding:"10px 14px",background:"#fef2f2",border:"1px solid #fecaca",borderRadius:8,display:"flex",gap:10,alignItems:"flex-start"}}>
                    <span style={{fontSize:16,flexShrink:0}}>🔴</span>
                    <span style={{fontSize:12,color:"#991b1b",fontWeight:500}}>Si le GPS refuse, tu n'es pas dans la zone. Contacte ton contremaître.</span>
                  </div>
                </div>
              );
            })()}

            {/* Section 2 — Voyages */}
            {(()=>{
              const steps=[
                ["📜","Faire défiler vers le bas","Jusqu'à Journal de chantier (disponible dès que pointé)"],
                ["🚚","Choisir ENTRANT ou SORTANT","Camion qui arrive → ENTRANT · Camion qui quitte → SORTANT"],
                ["🪨","Sélectionner le matériau","Voir la liste de référence à la section 4"],
                ["⚖️","Tonnage — 15 t.m. par défaut","Ajuster si le chargement est différent"],
                ["🔴","Appuyer sur le bouton rouge","✓ confirme l'enregistrement"],
              ];
              return (
                <div style={{borderRadius:10,overflow:"hidden",border:"1px solid var(--border)",background:"var(--surface)"}}>
                  <div style={{padding:"14px 18px",background:"var(--bg-2)",display:"flex",alignItems:"center",gap:12,borderBottom:"1px solid var(--border)"}}>
                    <span style={{width:28,height:28,borderRadius:"50%",background:"var(--red)",color:"#fff",fontWeight:700,fontSize:13,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>2</span>
                    <span style={{fontFamily:"var(--font-display)",fontWeight:700,fontSize:15}}>Journal de chantier — Voyages</span>
                  </div>
                  {steps.map(([icon,title,desc],i)=>(
                    <div key={i} style={{display:"flex",alignItems:"center",gap:14,padding:"13px 18px",borderBottom:i<steps.length-1?"1px solid var(--border)":"none"}}>
                      <span style={{fontSize:20,flexShrink:0,width:28,textAlign:"center"}}>{icon}</span>
                      <div>
                        <div style={{fontWeight:600,fontSize:13}}>{title}</div>
                        <div style={{fontSize:12,color:"var(--text-3)",marginTop:2}}>{desc}</div>
                      </div>
                    </div>
                  ))}
                  <div style={{margin:"0 18px 14px",padding:"10px 14px",background:"#fffbeb",border:"1px solid #fde68a",borderRadius:8,display:"flex",gap:10,alignItems:"flex-start"}}>
                    <span style={{fontSize:16,flexShrink:0}}>⚠️</span>
                    <span style={{fontSize:12,color:"#92400e",fontWeight:500}}>Enregistrer chaque voyage dès l'arrivée ou le départ — ne pas attendre à la fin de la journée.</span>
                  </div>
                </div>
              );
            })()}

            {/* Section 3 — Fin de journée */}
            {(()=>{
              const steps=[
                ["🏁","Pointer la sortie","Appuyer sur le bouton Pointer la sortie"],
                ["☕","Pause prise","Cocher si une pause de 30 min a été prise"],
                ["📊","Répartir les heures","Entre les phases — le total doit correspondre aux heures travaillées"],
                ["🔧","Type de tâche","Indiquer Main d'œuvre ou Opération d'équipement"],
                ["📝","Notes et Soumettre","Ajouter des notes si nécessaire, puis soumettre"],
              ];
              return (
                <div style={{borderRadius:10,overflow:"hidden",border:"1px solid var(--border)",background:"var(--surface)"}}>
                  <div style={{padding:"14px 18px",background:"var(--bg-2)",display:"flex",alignItems:"center",gap:12,borderBottom:"1px solid var(--border)"}}>
                    <span style={{width:28,height:28,borderRadius:"50%",background:"var(--red)",color:"#fff",fontWeight:700,fontSize:13,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>3</span>
                    <span style={{fontFamily:"var(--font-display)",fontWeight:700,fontSize:15}}>Fin de journée — Départ</span>
                  </div>
                  {steps.map(([icon,title,desc],i)=>(
                    <div key={i} style={{display:"flex",alignItems:"center",gap:14,padding:"13px 18px",borderBottom:i<steps.length-1?"1px solid var(--border)":"none"}}>
                      <span style={{fontSize:20,flexShrink:0,width:28,textAlign:"center"}}>{icon}</span>
                      <div>
                        <div style={{fontWeight:600,fontSize:13}}>{title}</div>
                        <div style={{fontSize:12,color:"var(--text-3)",marginTop:2}}>{desc}</div>
                      </div>
                    </div>
                  ))}
                  <div style={{margin:"0 18px 14px",padding:"10px 14px",background:"#f0fdf4",border:"1px solid #bbf7d0",borderRadius:8,display:"flex",gap:10,alignItems:"flex-start"}}>
                    <span style={{fontSize:16,flexShrink:0}}>✅</span>
                    <span style={{fontSize:12,color:"#166534",fontWeight:500}}>Toujours pointer avant de quitter. Si oubli, aviser le contremaître le lendemain.</span>
                  </div>
                </div>
              );
            })()}

            {/* Section 4 — Matériaux référence */}
            <div style={{borderRadius:10,overflow:"hidden",border:"1px solid var(--border)",background:"var(--surface)",marginBottom:24}}>
              <div style={{padding:"14px 18px",background:"var(--bg-2)",display:"flex",alignItems:"center",gap:12,borderBottom:"1px solid var(--border)"}}>
                <span style={{width:28,height:28,borderRadius:"50%",background:"var(--red)",color:"#fff",fontWeight:700,fontSize:13,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>4</span>
                <span style={{fontFamily:"var(--font-display)",fontWeight:700,fontSize:15}}>Matériaux — Référence rapide</span>
              </div>
              <div style={{padding:"14px 18px",borderBottom:"1px solid var(--border)"}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
                  <span style={{fontSize:16}}>🚛</span>
                  <span style={{fontWeight:700,fontSize:12,textTransform:"uppercase",letterSpacing:"0.06em",color:"var(--text-3)"}}>Entrants</span>
                </div>
                <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                  {["Gravier 0-3/4\"","Pierre nette 1/4\"","Pierre nette 1/2\"","Pierre nette 3/4\"","Sable classe A","Sable classe B","Poussière de pierre","Terre à gazon","Remblais","Pierre 4\"-8\"","Autre"].map(m=>(
                    <span key={m} style={{background:"var(--bg-2)",border:"1px solid var(--border)",borderRadius:6,padding:"4px 10px",fontSize:12,color:"var(--text-2)"}}>{m}</span>
                  ))}
                </div>
              </div>
              <div style={{padding:"14px 18px"}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
                  <span style={{fontSize:16}}>⛏️</span>
                  <span style={{fontWeight:700,fontSize:12,textTransform:"uppercase",letterSpacing:"0.06em",color:"var(--text-3)"}}>Sortants</span>
                </div>
                <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                  {["Excavation","Souche","Béton propre","Béton contaminé","Asphalte","Autre"].map(m=>(
                    <span key={m} style={{background:"var(--bg-2)",border:"1px solid var(--border)",borderRadius:6,padding:"4px 10px",fontSize:12,color:"var(--text-2)"}}>{m}</span>
                  ))}
                </div>
              </div>
            </div>

          </div>
        </div>
      )}

      {(role!=="employee"||(role==="employee"&&activeGuideTab==="pointage"))&&(
      <div className="page-body">

        {/* ── MY SCHEDULE (employee only) ── */}
        {role==="employee"&&(()=>{
          const todayMs = new Date(); todayMs.setHours(0,0,0,0);
          const dow = todayMs.getDay(); // 0=Sun
          const diffToMon = dow===0?-6:1-dow;
          const thisMonday = new Date(todayMs); thisMonday.setDate(todayMs.getDate()+diffToMon);
          const thisFriday = new Date(thisMonday); thisFriday.setDate(thisMonday.getDate()+4);
          const nextMonday = new Date(thisMonday); nextMonday.setDate(thisMonday.getDate()+7);
          const nextFriday = new Date(nextMonday); nextFriday.setDate(nextMonday.getDate()+4);
          const toISO = d => d.toISOString().slice(0,10);
          const thisWeekStart = toISO(thisMonday);
          const thisWeekEnd   = toISO(thisFriday);
          const nextWeekStart = toISO(nextMonday);
          const nextWeekEnd   = toISO(nextFriday);
          const overlaps = (j, wStart, wEnd) =>
            j.startDate && j.endDate && j.startDate <= wEnd && j.endDate >= wStart &&
            (j.crewNames||[]).includes(myName);
          const thisWeekJobs = (jobs||[]).filter(j=>overlaps(j,thisWeekStart,thisWeekEnd));
          const nextWeekJobs = (jobs||[]).filter(j=>overlaps(j,nextWeekStart,nextWeekEnd));
          const fmtDateRange = (s,e) => {
            const opts = {day:"numeric",month:"short"};
            const loc = lang==="fr"?"fr-CA":"en-CA";
            if (!s) return "—";
            if (s===e||!e) return new Date(s+"T12:00:00").toLocaleDateString(loc,opts);
            return `${new Date(s+"T12:00:00").toLocaleDateString(loc,opts)} – ${new Date(e+"T12:00:00").toLocaleDateString(loc,opts)}`;
          };
          const statusStyle = st => {
            if (st==="inProgress") return {background:"#dbeafe",color:"#1d4ed8"};
            if (st==="completed") return {background:"#dcfce7",color:"#166534"};
            return {background:"#fef9c3",color:"#854d0e"};
          };
          const statusLabel = st => {
            if (st==="inProgress") return lang==="fr"?"En cours":"In progress";
            if (st==="completed") return lang==="fr"?"Terminé":"Completed";
            return lang==="fr"?"Planifié":"Planned";
          };
          const renderRows = (list) => list.length===0
            ? <div style={{fontSize:13,color:"var(--text-3)",padding:"6px 0"}}>{lang==="fr"?"Aucune assignation":"No assignments"}</div>
            : list.map(j=>(
              <div key={j.id} style={{display:"flex",alignItems:"center",gap:10,padding:"7px 0",borderBottom:"1px solid var(--border)"}}>
                <div style={{flex:1}}>
                  <div style={{fontWeight:600,fontSize:13}}>{j.title||"—"}</div>
                  <div style={{fontSize:11,color:"var(--text-3)",marginTop:2,display:"flex",alignItems:"center",gap:6}}>
                    <Icon name="calendar" size={10}/>{fmtDateRange(j.startDate,j.endDate)}
                    {j.siteLocation&&<><Icon name="mapPin" size={10}/>{j.siteLocation}</>}
                  </div>
                </div>
                <span style={{fontSize:11,padding:"2px 7px",borderRadius:4,fontWeight:600,...statusStyle(j.status)}}>{statusLabel(j.status)}</span>
              </div>
            ));
          return (
            <div className="card" style={{marginBottom:16,padding:"20px 24px"}}>
              <div style={{fontFamily:"var(--font-display)",fontWeight:700,fontSize:15,marginBottom:14,display:"flex",alignItems:"center",gap:8}}>
                <Icon name="calendar" size={16}/>
                {lang==="fr"?"Mon horaire":"My Schedule"}
              </div>
              <div style={{marginBottom:12}}>
                <div style={{fontSize:12,fontWeight:600,color:"var(--text-3)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:6}}>{lang==="fr"?"Cette semaine":"This week"}</div>
                {renderRows(thisWeekJobs)}
              </div>
              <div>
                <div style={{fontSize:12,fontWeight:600,color:"var(--text-3)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:6}}>{lang==="fr"?"Semaine prochaine":"Next week"}</div>
                {renderRows(nextWeekJobs)}
              </div>
            </div>
          );
        })()}

        {/* ── PUNCH CLOCK ── */}
        <div className="card" style={{marginBottom:16,padding:"20px 24px"}}>
          <div style={{fontFamily:"var(--font-display)",fontWeight:700,fontSize:15,marginBottom:14,display:"flex",alignItems:"center",gap:8}}>
            <Icon name="clock" size={16}/>
            {activeSession ? tt.active : tt.noActiveSession}
            {activeSession&&<span style={{marginLeft:8,fontSize:22,fontWeight:800,color:"var(--red)",fontVariantNumeric:"tabular-nums"}}>{fmtDuration(nowMs-new Date(activeSession.punchIn).getTime())}</span>}
            {activeSession?.needsAdminReview&&<span title={tt.needsReview} style={{color:"#ef4444",marginLeft:4}}>🔴</span>}
          </div>

          {role==="foreman"&&(
            <div className="form-group" style={{marginBottom:12}}>
              <label style={{fontSize:12,color:"var(--text-3)"}}>{tt.clockFor}</label>
              <select className="form-select" value={clockForId} onChange={e=>{setClockForId(e.target.value);setSelectedJobId("");setGpsStatus("idle");}}>
                <option value="self">{myName} ({lang==="fr"?"moi":"me"})</option>
                {teamMembers.map(m=><option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
          )}

          {!activeSession?(
            <div className="form-group">
              <label style={{fontSize:12,color:"var(--text-3)"}}>{tt.job}</label>
              <select className="form-select" value={selectedJobId} onChange={e=>handleJobSelect(e.target.value)}>
                <option value="">{tt.selectJob}</option>
                {jobs.filter(j=>j.status==="inProgress"||j.status==="pending").map(j=><option key={j.id} value={j.id}>{j.jobTypeTag==="warranty"?"🔧 ":""}{j.title}</option>)}
              </select>
            </div>
          ):(
            <div style={{fontSize:13,color:"var(--text-2)",marginBottom:12}}>
              <strong>{activeSession.jobTitle}</strong>
              <span style={{marginLeft:8,fontSize:11,color:"var(--text-3)"}}>
                {tt.punchIn}: {new Date(activeSession.punchIn).toLocaleTimeString("fr-CA",{hour:"2-digit",minute:"2-digit"})}
                {activeSession.snappedToStandard&&<span style={{marginLeft:6,color:"var(--green)",fontWeight:600}}>⌚ standard</span>}
              </span>
            </div>
          )}

          {selectedJobId&&!activeSession&&jobs.find(j=>j.id===selectedJobId)?.siteLocation?.lat&&(
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10,fontSize:12}}>
              <Icon name="mapPin" size={13}/>
              {gpsStatus==="checking"&&<span style={{color:"var(--text-3)"}}>{tt.gpsCheck}</span>}
              {gpsStatus==="ok"&&<span style={{color:"var(--green)"}}>{tt.gpsOk}</span>}
              {gpsStatus==="off"&&<><span style={{color:"var(--red)"}}>{tt.gpsOff}</span><button className="btn btn-secondary btn-sm" onClick={()=>setGpsOverride(true)}>{tt.bypassGps}</button></>}
              {gpsStatus==="error"&&<><span style={{color:"var(--text-3)"}}>{tt.gpsError}</span><button className="btn btn-secondary btn-sm" onClick={()=>setGpsOverride(true)}>{tt.bypassGps}</button></>}
            </div>
          )}

          <div style={{display:"flex",gap:8,marginTop:8,alignItems:"center"}}>
            {!activeSession
              ?(()=>{
                const settingsReady = settings && 'standardStartTime' in settings;
                return <button className="btn btn-primary" disabled={!canPunchIn||actionPending||!settingsReady} onClick={handlePunchInClick}>
                  <Icon name="clock" size={14}/>
                  {settingsReady ? tt.clockIn : (lang==="fr" ? "Chargement..." : "Loading...")}
                </button>;
              })()
              :<button className="btn btn-danger" disabled={actionPending} onClick={handlePunchOutClick}><Icon name="clock" size={14}/>{tt.clockOut}</button>
            }
            {activeSession&&(role==="admin"||role==="foreman")&&(
              <button className="btn-icon" title="Corriger l'heure de départ / Override Punch-Out" style={{color:"#d97706"}} onClick={()=>handleOpenOverride(activeSession)}>
                <Icon name="clock" size={15}/>
              </button>
            )}
          </div>
        </div>

        {/* ── JOURNAL DE CHANTIER (active punch) ── */}
        {activeSession&&(
          <div className="card" style={{marginBottom:16}}>
            <div style={{fontFamily:"var(--font-display)",fontWeight:700,fontSize:12,color:"var(--text-3)",textTransform:"uppercase",letterSpacing:1,marginBottom:12}}>Journal de chantier</div>
            <TruckLog
              jobId={activeSession.jobId}
              jobTitle={activeSession.jobTitle}
              myName={myName}
              canEdit={true}
              lang={lang}
            />
          </div>
        )}

        {/* ── ADMIN/FOREMAN TABS ── */}
        {(role==="admin"||role==="foreman")&&(
          <div>
            <div style={{display:"flex",gap:2,marginBottom:16,borderBottom:"2px solid var(--border)"}}>
              {[["pointages",tt.tabPointages],["revision",tt.tabRevision],["sommaire",tt.tabSommaire]].map(([id,label])=>(
                <button key={id} onClick={()=>setActiveTab(id)} style={{padding:"8px 18px",fontSize:13,fontWeight:600,border:"none",background:"none",cursor:"pointer",borderBottom:`2px solid ${activeTab===id?"var(--red)":"transparent"}`,color:activeTab===id?"var(--red)":"var(--text-3)",marginBottom:-2}}>{label}</button>
              ))}
              {activeTab==="sommaire"&&<button className="btn btn-secondary btn-sm" style={{marginLeft:"auto",marginBottom:4}} onClick={exportCsv}><Icon name="reports" size={13}/>{tt.csvExport}</button>}
            </div>

            {/* TAB 1 — Pointages */}
            {activeTab==="pointages"&&(()=>{
              const live = timesheets.filter(ts=>!ts.punchOut);
              const todayStr = new Date().toISOString().slice(0,10);
              const today = timesheets.filter(ts=>ts.punchOut&&ts.punchIn?.slice(0,10)===todayStr).sort((a,b)=>(b.punchIn||"").localeCompare(a.punchIn||""));
              return (
                <div>
                  {live.length>0&&(
                    <div className="card" style={{marginBottom:12}}>
                      <div style={{fontWeight:700,fontSize:13,marginBottom:10,color:"var(--red)"}}>{tt.liveNow}</div>
                      {live.map(ts=>(
                        <div key={ts.id} className="list-row" style={{borderLeft:"3px solid var(--red)",paddingLeft:12}}>
                          <div style={{flex:1}}>
                            <div style={{fontWeight:600,fontSize:13,display:"flex",gap:8,alignItems:"center"}}>
                              {ts.employeeName}<span style={{fontSize:11,color:"var(--text-3)"}}>{ts.jobTitle}</span>
                              {ts.needsAdminReview&&<span title={tt.needsReview} style={{color:"#ef4444"}}>🔴</span>}
                            </div>
                            <div style={{fontSize:12,color:"var(--text-3)"}}>
                              {tt.punchIn}: {ts.punchIn?new Date(ts.punchIn).toLocaleTimeString("fr-CA",{hour:"2-digit",minute:"2-digit"}):""} · {tt.elapsed}: <strong style={{color:"var(--red)"}}>{fmtDuration(nowMs-new Date(ts.punchIn).getTime())}</strong>
                            </div>
                          </div>
                          {(role==="admin"||role==="foreman")&&(
                            <button className="btn-icon" title="Corriger l'heure de départ / Override Punch-Out" style={{marginLeft:8,color:"#d97706"}} onClick={()=>handleOpenOverride(ts)}>
                              <Icon name="clock" size={15}/>
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="card">
                    <div style={{fontWeight:700,fontSize:13,marginBottom:10}}>{tt.todayCompleted}</div>
                    {today.length===0&&<div style={{fontSize:13,color:"var(--text-3)"}}>{tt.noTimesheets}</div>}
                    {today.map(ts=>{
                      const hrs=(ts.roundedDuration||0)/3600000; const isOT=hrs>8; const ss=statusStyle(ts.status);
                      return (
                        <div key={ts.id} className="list-row" style={isOT?{background:"#fffbeb"}:{}}>
                          <div style={{flex:1}}>
                            <div style={{fontWeight:600,fontSize:13,display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                              {ts.employeeName}<span style={{fontSize:11,color:"var(--text-3)"}}>{ts.jobTitle}</span>
                              <span style={{fontSize:11,fontWeight:600,padding:"2px 8px",borderRadius:10,...ss}}>{tt[ts.status]||ts.status}</span>
                              {isOT&&<span style={{fontSize:11,fontWeight:700,color:"#d97706",background:"#fef3c7",padding:"2px 6px",borderRadius:8}}>{tt.overtimeFlag}</span>}
                              {ts.needsAdminReview&&<span style={{color:"#ef4444"}}>🔴</span>}
                            </div>
                            <div style={{fontSize:12,color:"var(--text-3)"}}>
                              {hrs.toFixed(2)}h · {new Date(ts.punchIn).toLocaleTimeString("fr-CA",{hour:"2-digit",minute:"2-digit"})} → {new Date(ts.punchOut).toLocaleTimeString("fr-CA",{hour:"2-digit",minute:"2-digit"})}
                              {ts.breakTaken&&<span style={{marginLeft:8}}>· pause ✓</span>}
                            </div>
                          </div>
                          {(ts.status==="draft"||ts.status==="submitted")&&ts.punchOut&&(
                            <button className="btn btn-secondary btn-sm" style={{color:"var(--green)",borderColor:"var(--green)"}} onClick={()=>updateTimesheet(ts.id,{...ts,status:"approved",approvedBy:myName,approvedAt:new Date().toISOString()})}>{tt.approve}</button>
                          )}
                          {(role==="admin"||role==="foreman")&&ts.punchOut&&<button className="btn-icon" title="Modifier la répartition" style={{marginLeft:4}} onClick={()=>handleEditAllocation(ts)}><Icon name="edit" size={15}/></button>}
                          {role==="admin"&&<button className="btn-icon danger" title={t.common.delete} style={{marginLeft:4}} onClick={()=>deleteTimesheet(ts.id)}><Icon name="trash" size={13}/></button>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* TAB 2 — Révision */}
            {activeTab==="revision"&&(()=>{
              const shiftRevDate = (n) => {
                const d = new Date(revDate+"T12:00:00"); d.setDate(d.getDate()+n);
                const s = d.toISOString().slice(0,10);
                setRevDate(s); setSelectedTsIds(new Set());
              };
              const fmtRevDate = (ds) => {
                const d = new Date(ds+"T12:00:00");
                const loc = lang==="fr"?"fr-CA":"en-CA";
                const s = d.toLocaleDateString(loc,{weekday:"long",day:"numeric",month:"long",year:"numeric"});
                return s.charAt(0).toUpperCase()+s.slice(1);
              };
              const roleBadgeEl = (empId) => {
                const m = teamMembers.find(m=>m.id===empId);
                if (!m?.role) return null;
                const labels={admin:"Admin",foreman:"Contremaître",employee:"Employé"};
                const colors={admin:{background:"#ede9fe",color:"#6d28d9"},foreman:{background:"#dbeafe",color:"#1d4ed8"},employee:{background:"#f3f4f6",color:"#6b7280"}};
                const c=colors[m.role]||colors.employee;
                return <span style={{fontSize:10,fontWeight:600,padding:"1px 6px",borderRadius:6,...c}}>{labels[m.role]||m.role}</span>;
              };
              const dayAll = timesheets.filter(ts=>ts.punchIn?.slice(0,10)===revDate);
              const dayFiltered = dayAll.filter(ts=>{
                if (filterEmployee&&!ts.employeeName?.toLowerCase().includes(filterEmployee.toLowerCase())) return false;
                if (filterJob&&ts.jobId!==filterJob) return false;
                return true;
              });
              const statusOrder = s=>s==="approved"?1:s==="rejected"?2:0;
              const dayTimesheets = [...dayFiltered].sort((a,b)=>statusOrder(a.status)-statusOrder(b.status));
              const dayPending = dayAll.filter(ts=>ts.status==="draft"||ts.status==="submitted").length;
              // Alerts
              const alerts = [];
              timesheets.filter(ts=>!ts.punchOut&&ts.punchIn?.slice(0,10)===revDate).forEach(ts=>{
                alerts.push({id:ts.id,emoji:"🔴",msg:`${ts.employeeName} — ${lang==="fr"?"Aucune heure de sortie enregistrée":"No punch-out recorded"}`});
              });
              dayAll.filter(ts=>(ts.roundedDuration||0)/3600000>10).forEach(ts=>{
                alerts.push({id:ts.id,emoji:"🟠",msg:`${ts.employeeName} — ${((ts.roundedDuration||0)/3600000).toFixed(1)}h · ${lang==="fr"?"Dépassement significatif":"Significant overtime"}`});
              });
              dayAll.filter(ts=>ts.status==="rejected").forEach(ts=>{
                alerts.push({id:ts.id,emoji:"🟡",msg:`${ts.employeeName} — ${lang==="fr"?"Refusé — en attente de resoumission":"Rejected — awaiting resubmission"}`});
              });
              // Bulk approve
              const bulkEligible = dayTimesheets.filter(ts=>(ts.status==="draft"||ts.status==="submitted")&&ts.punchOut&&ts.taskAllocations?.length);
              const allSelected = bulkEligible.length>0&&bulkEligible.every(ts=>selectedTsIds.has(ts.id));
              const toggleSelected = (id) => setSelectedTsIds(prev=>{const n=new Set(prev);n.has(id)?n.delete(id):n.add(id);return n;});
              const bulkApprove = async()=>{
                const toApprove=bulkEligible.filter(ts=>selectedTsIds.has(ts.id));
                if (!toApprove.length) return;
                setActionPending(true);
                await Promise.all(toApprove.map(ts=>updateTimesheet(ts.id,{...ts,status:"approved",approvedBy:myName,approvedAt:new Date().toISOString()})));
                setSelectedTsIds(new Set()); setActionPending(false);
              };
              return (
                <div>
                  {/* Date Nav Bar */}
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12,background:"var(--surface)",border:"1px solid var(--border)",borderRadius:"var(--radius-sm)",padding:"10px 14px"}}>
                    <button className="btn-icon" onClick={()=>shiftRevDate(-1)}><span style={{display:"inline-flex",transform:"rotate(180deg)"}}><Icon name="chevronRight" size={16}/></span></button>
                    <div style={{flex:1,textAlign:"center",position:"relative",cursor:"pointer"}} onClick={()=>revDateInputRef.current?.showPicker?.()??revDateInputRef.current?.focus?.()}>
                      <div style={{fontWeight:700,fontSize:14}}>{fmtRevDate(revDate)}</div>
                      <input type="date" ref={revDateInputRef} value={revDate} onChange={e=>{if(e.target.value){setRevDate(e.target.value);setSelectedTsIds(new Set());}}} style={{position:"absolute",opacity:0,pointerEvents:"none",top:0,left:"50%",width:1,height:1}}/>
                    </div>
                    <div style={{flexShrink:0}}>
                      {dayPending>0
                        ?<span style={{fontSize:11,fontWeight:600,padding:"3px 9px",borderRadius:10,background:"#fef3c7",color:"#d97706",whiteSpace:"nowrap"}}>{dayPending} {lang==="fr"?"en attente":"pending"}</span>
                        :<span style={{fontSize:11,fontWeight:600,padding:"3px 9px",borderRadius:10,background:"#dcfce7",color:"#166534",whiteSpace:"nowrap"}}>{lang==="fr"?"Tout approuvé":"All approved"}</span>
                      }
                    </div>
                    <button className="btn-icon" onClick={()=>shiftRevDate(1)}><Icon name="chevronRight" size={16}/></button>
                  </div>

                  {/* Secondary filters */}
                  <div style={{display:"flex",gap:8,marginBottom:12,flexWrap:"wrap"}}>
                    <input className="search-input" style={{maxWidth:180}} placeholder={tt.allEmployees} value={filterEmployee} onChange={e=>setFilterEmployee(e.target.value)}/>
                    <select className="filter-select" value={filterJob} onChange={e=>setFilterJob(e.target.value)}>
                      <option value="">{tt.allJobs}</option>
                      {jobs.map(j=><option key={j.id} value={j.id}>{j.title}</option>)}
                    </select>
                  </div>

                  {/* Add manual entry — admin only */}
                  {role==="admin"&&(
                    <div style={{display:"flex",justifyContent:"flex-end",marginBottom:12}}>
                      <button className="btn btn-secondary btn-sm" onClick={()=>{setManualEntryForm({employeeId:"",jobId:"",date:revDate,punchIn:"",punchOut:"",breakTaken:false,notes:""});setManualEntryError("");setManualEntryModal(true);}}>
                        <Icon name="plus" size={13}/> Ajouter une entrée / Add Entry
                      </button>
                    </div>
                  )}

                  {/* Payroll Alerts */}
                  {alerts.length>0&&(
                    <div style={{background:"#fffbeb",border:"1px solid #fcd34d",borderRadius:"var(--radius-sm)",padding:"10px 14px",marginBottom:12}}>
                      <div style={{fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:1,color:"#92400e",marginBottom:6}}>⚠ Alertes / Alerts</div>
                      {alerts.map((a,i)=>(
                        <div key={i} style={{fontSize:12,color:"#78350f",marginBottom:3,cursor:"pointer"}} onClick={()=>document.getElementById(`ts-card-${a.id}`)?.scrollIntoView({behavior:"smooth",block:"center"})}>
                          {a.emoji} {a.msg}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Employee Cards */}
                  {dayTimesheets.length===0&&(
                    <div className="card" style={{textAlign:"center",padding:"32px 16px",color:"var(--text-3)",fontSize:13}}>
                      {lang==="fr"?"Aucun pointage pour cette journée.":"No timesheets for this day."}
                    </div>
                  )}
                  <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:bulkEligible.length>=2?72:0}}>
                    {dayTimesheets.map(ts=>{
                      const hrs=(ts.roundedDuration||0)/3600000;
                      const isOT=hrs>8;
                      const ss=statusStyle(ts.status);
                      const isPending=ts.status==="draft"||ts.status==="submitted";
                      const job=jobs.find(j=>j.id===ts.jobId);
                      const totalBudget=(job?.phases||[]).reduce((a,p)=>a+(p.labourHours||0),0);
                      const totalLogged=totalBudget>0?timesheets.filter(t=>t.jobId===ts.jobId&&t.status==="approved").reduce((a,t)=>(t.taskAllocations||[]).reduce((b,al)=>b+(parseFloat(al.hours)||0),0)+a,0):0;
                      const budgetRatio=totalBudget>0?totalLogged/totalBudget:0;
                      const budgetColor=budgetRatio>=1?"var(--red)":budgetRatio>=0.8?"#d97706":"#16a34a";
                      const needsAlloc=ts.needsAdminReview&&ts.overrideLog&&!(ts.taskAllocations?.length);
                      const inBulk=bulkEligible.some(b=>b.id===ts.id);

                      if (ts.status==="approved") return (
                        <div id={`ts-card-${ts.id}`} key={ts.id} className="card" style={{padding:"12px 16px",background:"var(--bg-2)"}}>
                          <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                            <div style={{flex:1,minWidth:0}}>
                              <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                                <span style={{fontWeight:600,fontSize:13}}>{ts.employeeName}</span>
                                {roleBadgeEl(ts.employeeId)}
                                <span style={{fontSize:11,fontWeight:600,padding:"2px 8px",borderRadius:10,...ss}}>{tt[ts.status]||ts.status}</span>
                                <span style={{fontSize:11,color:"var(--text-3)"}}>
                                  {ts.punchIn?new Date(ts.punchIn).toLocaleTimeString("fr-CA",{hour:"2-digit",minute:"2-digit"}):""} → {ts.punchOut?new Date(ts.punchOut).toLocaleTimeString("fr-CA",{hour:"2-digit",minute:"2-digit"}):""} · {toHM(hrs)}{ts.breakTaken?" · pause ✓":""}
                                </span>
                                <span style={{fontSize:11,color:"var(--text-3)"}}>{ts.jobTitle}</span>
                              </div>
                              {ts.createdManually&&<span style={{fontSize:10,fontWeight:600,padding:"1px 7px",borderRadius:6,background:"#dbeafe",color:"#1d4ed8"}}>Entrée manuelle / Manual entry</span>}
                              {ts.approvedBy&&<div style={{fontSize:11,color:"var(--text-3)",marginTop:2}}>Approuvé par {ts.approvedBy.split?.(" (")[0]??ts.approvedBy}</div>}
                            </div>
                            <button className="btn-icon" title="Annuler approbation / Undo" onClick={()=>updateTimesheet(ts.id,{...ts,status:"submitted",approvedBy:null,approvedAt:null})}><Icon name="x" size={13}/></button>
                            {role==="admin"&&ts.punchOut&&<button className="btn-icon" title="Modifier les heures / Edit Times" onClick={()=>handleOpenEditTimes(ts)}><Icon name="edit" size={13}/></button>}
                            {role==="admin"&&<button className="btn-icon danger" title={t.common.delete} onClick={()=>deleteTimesheet(ts.id)}><Icon name="trash" size={13}/></button>}
                          </div>
                        </div>
                      );

                      return (
                        <div id={`ts-card-${ts.id}`} key={ts.id} className="card" style={{padding:0,overflow:"hidden",border:`1px solid ${isOT?"#fcd34d":"var(--border)"}`,background:isOT?"#fffbeb":"var(--surface)"}}>
                          <div style={{padding:"14px 16px 10px"}}>
                            {/* Header row */}
                            <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap",marginBottom:7}}>
                              <span style={{fontWeight:700,fontSize:14}}>{ts.employeeName}</span>
                              {roleBadgeEl(ts.employeeId)}
                              <span style={{fontSize:11,fontWeight:600,padding:"2px 8px",borderRadius:10,...ss}}>{tt[ts.status]||ts.status}</span>
                              {ts.status==="submitted"&&(ts.revisionHistory||[]).some(h=>h.action==="rejected")&&<span style={{fontSize:11,fontWeight:600,padding:"2px 8px",borderRadius:10,background:"#fef3c7",color:"#d97706"}}>{tt.resubmitted}</span>}
                              {isOT&&<span style={{fontSize:11,fontWeight:700,color:"#d97706",background:"#fef3c7",padding:"2px 6px",borderRadius:8}}>⚡ {tt.overtimeFlag}</span>}
                              {ts.needsAdminReview&&<span style={{color:"#ef4444",fontSize:12}}>🔴</span>}
                              {ts.createdManually&&<span style={{fontSize:10,fontWeight:600,padding:"1px 7px",borderRadius:6,background:"#dbeafe",color:"#1d4ed8"}}>Entrée manuelle / Manual entry</span>}
                            </div>
                            {/* Time row */}
                            <div style={{fontSize:12,color:"var(--text-2)",marginBottom:4,display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                              <Icon name="clock" size={11}/>
                              <span>{ts.punchIn?new Date(ts.punchIn).toLocaleTimeString("fr-CA",{hour:"2-digit",minute:"2-digit"}):""} → {ts.punchOut?new Date(ts.punchOut).toLocaleTimeString("fr-CA",{hour:"2-digit",minute:"2-digit"}):<strong style={{color:"var(--red)"}}>en cours</strong>}</span>
                              <strong>· {toHM(hrs)}</strong>
                              {ts.breakTaken&&<span style={{color:"var(--text-3)"}}>· pause ✓</span>}
                              {ts.needsAdminReview&&ts.punchInRaw&&ts.punchInFinal&&ts.punchInRaw!==ts.punchInFinal&&<span style={{color:"#ef4444",fontSize:11}}>({new Date(ts.punchInRaw).toLocaleTimeString("fr-CA",{hour:"2-digit",minute:"2-digit"})} réel)</span>}
                            </div>
                            {/* Job row */}
                            <div style={{fontSize:12,color:"var(--text-3)",marginBottom:6,display:"flex",gap:6,alignItems:"center"}}>
                              <Icon name="jobs" size={11}/>
                              <span>{ts.jobTitle}</span>
                            </div>
                            {/* Phase allocations */}
                            {(ts.taskAllocations||[]).length>0&&(
                              <div style={{display:"flex",flexWrap:"wrap",gap:"4px 10px",marginBottom:6}}>
                                {(ts.taskAllocations||[]).map((a,i)=>(
                                  <span key={i} style={{fontSize:11,background:"var(--bg-2)",border:"1px solid var(--border)",borderRadius:4,padding:"2px 7px",color:"var(--text-2)"}}>
                                    {a.phaseName} <strong>{toHM(parseFloat(a.hours)||0)}</strong>{a.type==="equipment"&&<span style={{color:"var(--text-3)"}}> ⚙</span>}
                                  </span>
                                ))}
                              </div>
                            )}
                            {/* Budget hint */}
                            {totalBudget>0&&(
                              <div style={{fontSize:11,color:budgetColor,marginBottom:5}}>
                                📊 {toHM(totalLogged)} / {toHM(totalBudget)} {lang==="fr"?"budgété":"budgeted"}
                              </div>
                            )}
                            {/* Override audit note */}
                            {ts.needsAdminReview&&ts.overrideLog&&(
                              <div style={{fontSize:11,color:"#d97706",marginBottom:4}}>
                                ✏️ Heure de départ corrigée par {ts.overrideLog.overriddenBy?.split(" (")[0]} à {new Date(ts.overrideLog.overriddenAt).toLocaleTimeString("fr-CA",{hour:"2-digit",minute:"2-digit"})} / Punch-out overridden by {ts.overrideLog.overriddenBy?.split(" (")[0]} at {new Date(ts.overrideLog.overriddenAt).toLocaleTimeString("fr-CA",{hour:"2-digit",minute:"2-digit"})}
                              </div>
                            )}
                            {/* Realloc warning */}
                            {needsAlloc&&isPending&&(
                              <div style={{fontSize:11,color:"#b91c1c",background:"#fee2e2",border:"1px solid #fca5a5",borderRadius:4,padding:"4px 8px",marginBottom:5}}>
                                Les allocations ont été effacées suite à la correction — réallouer avant d'approuver. / Phase allocations cleared — re-allocate before approving.
                              </div>
                            )}
                            {/* Rejection reason */}
                            {ts.status==="rejected"&&ts.rejectionReason&&(
                              <div style={{fontSize:12,padding:"8px 10px",background:"#fee2e2",borderRadius:"var(--radius-sm)",marginBottom:4,color:"#b91c1c"}}>
                                <strong>Refus: </strong>{ts.rejectionReason}
                              </div>
                            )}
                            {/* Day notes */}
                            {ts.dayNotes&&<div style={{fontSize:11,fontStyle:"italic",color:"var(--text-3)",marginTop:2}}>{ts.dayNotes}</div>}
                          </div>
                          {/* Card footer */}
                          <div style={{display:"flex",alignItems:"center",gap:6,padding:"8px 16px",borderTop:"1px solid var(--border)",background:"var(--bg)",flexWrap:"wrap"}}>
                            {isPending&&ts.punchOut&&inBulk&&(
                              <input type="checkbox" checked={selectedTsIds.has(ts.id)} onChange={()=>toggleSelected(ts.id)}/>
                            )}
                            {ts.status==="rejected"&&<span style={{fontSize:11,fontWeight:600,color:"#b91c1c",background:"#fee2e2",padding:"2px 8px",borderRadius:8}}>En attente de resoumission / Awaiting resubmission</span>}
                            <div style={{flex:1}}/>
                            {isPending&&ts.punchOut&&(
                              needsAlloc
                                ?<button className="btn btn-secondary btn-sm" style={{color:"#d97706",borderColor:"#d97706"}} onClick={()=>handleOverrideAllocate(ts)}>Allouer les heures / Allocate Hours</button>
                                :<button className="btn btn-secondary btn-sm" style={{color:"#16a34a",borderColor:"#16a34a"}} onClick={()=>updateTimesheet(ts.id,{...ts,status:"approved",approvedBy:myName,approvedAt:new Date().toISOString()})}>{tt.approve}</button>
                            )}
                            {isPending&&ts.punchOut&&<button className="btn btn-secondary btn-sm" style={{color:"var(--red)",borderColor:"var(--red)"}} onClick={()=>handleOpenReject(ts)}>{tt.reject}</button>}
                            {(role==="admin"||role==="foreman")&&ts.punchOut&&<button className="btn-icon" title="Modifier la répartition" onClick={()=>handleEditAllocation(ts)}><Icon name="edit" size={15}/></button>}
                            {role==="admin"&&ts.punchOut&&<button className="btn-icon" title="Modifier les heures / Edit Times" onClick={()=>handleOpenEditTimes(ts)}><Icon name="clock" size={15}/></button>}
                            {role==="admin"&&<button className="btn-icon danger" title={t.common.delete} onClick={()=>deleteTimesheet(ts.id)}><Icon name="trash" size={13}/></button>}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Bulk Approve Bar */}
                  {bulkEligible.length>=2&&(
                    <div style={{position:"sticky",bottom:0,zIndex:10,background:"var(--surface)",border:"1px solid var(--border)",borderRadius:"var(--radius-sm)",padding:"10px 16px",display:"flex",alignItems:"center",gap:10,boxShadow:"0 -2px 8px rgba(0,0,0,.08)",marginTop:8}}>
                      <label style={{display:"flex",alignItems:"center",gap:6,fontSize:12,fontWeight:600,cursor:"pointer"}}>
                        <input type="checkbox" checked={allSelected} onChange={()=>{allSelected?setSelectedTsIds(new Set()):setSelectedTsIds(new Set(bulkEligible.map(ts=>ts.id)));}}/>
                        {lang==="fr"?"Tout sélectionner":"Select all"}
                      </label>
                      <div style={{flex:1}}/>
                      {selectedTsIds.size>0&&<span style={{fontSize:12,color:"var(--text-3)"}}>{selectedTsIds.size} sélectionné(s)</span>}
                      <button className="btn btn-sm" style={{background:"#16a34a",borderColor:"#16a34a",color:"#fff",fontWeight:600}} disabled={selectedTsIds.size===0||actionPending} onClick={bulkApprove}>
                        <Icon name="check" size={13}/> {lang==="fr"?"Approuver la sélection":"Approve selected"}
                      </button>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* TAB 3 — Sommaire */}
            {activeTab==="sommaire"&&(
              <div>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12,fontSize:12,color:"var(--text-3)"}}>
                  Semaine du <input type="date" className="form-input" style={{width:150,fontSize:12}} value={summWeekStart} onChange={e=>setSummWeekStart(e.target.value)}/>
                </div>
                {summHasUnapproved&&<div style={{background:"#fee2e2",border:"1px solid #fca5a5",borderRadius:"var(--radius-sm)",padding:"10px 14px",marginBottom:12,fontSize:13,fontWeight:600,color:"#b91c1c"}}>{tt.blockedSommaire}</div>}
                {!summHasUnapproved&&(
                  <>
                    <div className="card" style={{marginBottom:12,overflowX:"auto"}}>
                      <div style={{fontWeight:700,fontSize:13,marginBottom:10}}>{tt.weeklyHours}</div>
                      <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                        <thead><tr>
                          <th style={{textAlign:"left",padding:"6px 10px",borderBottom:"1px solid var(--border)",color:"var(--text-3)",fontWeight:600}}>Employé</th>
                          {summWeekDays.map(d=><th key={d} style={{textAlign:"center",padding:"6px 8px",borderBottom:"1px solid var(--border)",color:"var(--text-3)",fontWeight:600,minWidth:60}}>{new Date(d+"T12:00:00").toLocaleDateString("fr-CA",{weekday:"short",day:"numeric"})}</th>)}
                          <th style={{textAlign:"center",padding:"6px 8px",borderBottom:"1px solid var(--border)",fontWeight:700}}>{tt.totalLabel}</th>
                        </tr></thead>
                        <tbody>
                          {summEmployees.map(emp=>{
                            const rowTotal=summWeekDays.reduce((a,d)=>a+cellHours(emp,d),0);
                            return (<tr key={emp}>
                              <td style={{padding:"6px 10px",fontWeight:600,borderBottom:"1px solid var(--border)"}}>{emp}</td>
                              {summWeekDays.map(d=>{ const h=cellHours(emp,d); return <td key={d} style={{textAlign:"center",padding:"6px 8px",borderBottom:"1px solid var(--border)",color:h>0?"var(--text)":"var(--text-3)"}}>{h>0?h.toFixed(2):"—"}</td>; })}
                              <td style={{textAlign:"center",padding:"6px 8px",fontWeight:700,borderBottom:"1px solid var(--border)"}}>{rowTotal.toFixed(2)}</td>
                            </tr>);
                          })}
                          <tr style={{background:"var(--bg)"}}>
                            <td style={{padding:"6px 10px",fontWeight:700}}>Total</td>
                            {summWeekDays.map(d=>{ const ct=summEmployees.reduce((a,e)=>a+cellHours(e,d),0); return <td key={d} style={{textAlign:"center",padding:"6px 8px",fontWeight:700}}>{ct>0?ct.toFixed(2):"—"}</td>; })}
                            <td style={{textAlign:"center",padding:"6px 8px",fontWeight:700}}>{summEmployees.reduce((a,e)=>a+summWeekDays.reduce((b,d)=>b+cellHours(e,d),0),0).toFixed(2)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div className="card" style={{marginBottom:12}}>
                      <div style={{fontWeight:700,fontSize:13,marginBottom:10}}>{tt.hoursByProject}</div>
                      {(()=>{ const byJob={}; summTimesheets.forEach(ts=>{if(!byJob[ts.jobId])byJob[ts.jobId]={title:ts.jobTitle,hours:0};byJob[ts.jobId].hours+=(ts.roundedDuration||0)/3600000;}); const rows=Object.values(byJob); if(!rows.length) return <div style={{fontSize:13,color:"var(--text-3)"}}>Aucune heure enregistrée</div>; return rows.map((j,i)=><div key={i} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:"1px solid var(--border)",fontSize:13}}><span>{j.title}</span><span style={{fontWeight:600}}>{j.hours.toFixed(2)}h</span></div>); })()}
                    </div>
                    <div className="card">
                      <div style={{fontWeight:700,fontSize:13,marginBottom:10}}>{tt.equipmentHours}</div>
                      {(()=>{ const byEquip={}; summTimesheets.forEach(ts=>(ts.taskAllocations||[]).filter(a=>a.type==="equipment"&&a.equipmentName).forEach(a=>{ const key=`${a.equipmentName}__${ts.jobId}`; if(!byEquip[key])byEquip[key]={equip:a.equipmentName,job:ts.jobTitle,hours:0}; byEquip[key].hours+=parseFloat(a.hours)||0; })); const rows=Object.values(byEquip); if(!rows.length) return <div style={{fontSize:13,color:"var(--text-3)"}}>Aucune heure d'équipement</div>; return rows.map((r,i)=><div key={i} style={{display:"flex",gap:12,padding:"6px 0",borderBottom:"1px solid var(--border)",fontSize:13}}><span style={{fontWeight:600,flex:1}}>{r.equip}</span><span style={{color:"var(--text-3)"}}>{r.job}</span><span style={{fontWeight:600}}>{r.hours.toFixed(2)}h</span></div>); })()}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* Employee own list */}
        {role==="employee"&&(
          <div className="card">
            {timesheets.filter(ts=>ts.employeeId===myId&&ts.punchOut).length===0
              ?<div className="empty-state"><div className="empty-icon"><Icon name="timesheets" size={36}/></div><div className="empty-title">{tt.noTimesheets}</div></div>
              :timesheets.filter(ts=>ts.employeeId===myId&&ts.punchOut).sort((a,b)=>(b.punchIn||"").localeCompare(a.punchIn||"")).map(ts=>{
                const hrs=(ts.roundedDuration||0)/3600000; const ss=statusStyle(ts.status);
                return (
                  <div key={ts.id} style={{borderBottom:"1px solid var(--border)",padding:"12px 16px"}}>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <div style={{flex:1}}>
                        <div style={{fontWeight:600,fontSize:13,display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                          {ts.jobTitle}
                          <span style={{fontSize:11,fontWeight:600,padding:"2px 8px",borderRadius:10,...ss}}>{tt[ts.status]||ts.status}</span>
                        </div>
                        <div style={{fontSize:12,color:"var(--text-3)"}}>{ts.punchIn?new Date(ts.punchIn).toLocaleDateString("fr-CA"):""} · {hrs.toFixed(2)}h{ts.breakTaken&&<span style={{marginLeft:8}}>· pause ✓</span>}</div>
                      </div>
                      {ts.status==="rejected"&&<button className="btn btn-secondary btn-sm" style={{color:"var(--red)",borderColor:"var(--red)",flexShrink:0}} onClick={()=>handleResubmit(ts)}>{tt.resubmit}</button>}
                    </div>
                    {ts.status==="rejected"&&ts.rejectionReason&&(
                      <div style={{marginTop:8,padding:"8px 10px",background:"#fee2e2",borderRadius:"var(--radius-sm)",fontSize:12,color:"#b91c1c"}}>
                        <strong>Refus: </strong>{ts.rejectionReason}
                      </div>
                    )}
                  </div>
                );
              })
            }
          </div>
        )}

      </div>
      )}

      {/* Standard time modal */}
      {timeModal&&(
        <div className="modal-backdrop" onClick={()=>setTimeModal(null)}>
          <div className="modal" style={{maxWidth:420}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title"><Icon name="clock" size={15}/> {tt.unusualTime}</div>
              <button className="btn-icon" title={t.common.close} onClick={()=>setTimeModal(null)}><Icon name="x"/></button>
            </div>
            <div className="modal-body">
              <div style={{fontSize:13,marginBottom:16,padding:"12px 14px",background:"var(--bg)",borderRadius:"var(--radius-sm)"}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}><span style={{color:"var(--text-3)"}}>{tt.standardStartLabel}:</span><strong>{timeModal.stdStr}</strong></div>
                <div style={{display:"flex",justifyContent:"space-between"}}><span style={{color:"var(--text-3)"}}>{tt.yourTime}:</span><strong>{timeModal.rawStr}</strong></div>
              </div>
              {[{val:"standard",label:tt.useStandard.replace("{time}",timeModal.stdStr)},{val:"rounded",label:tt.useRounded.replace("{time}",timeModal.roundedStr)},{val:"exact",label:tt.useExact.replace("{time}",timeModal.rawStr)}].map(opt=>(
                <label key={opt.val} style={{display:"flex",alignItems:"flex-start",gap:10,padding:"10px 12px",borderRadius:"var(--radius-sm)",border:`1px solid ${timeChoice===opt.val?"var(--red)":"var(--border)"}`,marginBottom:8,cursor:"pointer",fontSize:13,background:timeChoice===opt.val?"#fef2f2":""}}>
                  <input type="radio" value={opt.val} checked={timeChoice===opt.val} onChange={()=>setTimeChoice(opt.val)} style={{marginTop:2}}/>{opt.label}
                </label>
              ))}
              {timeChoice==="exact"&&(
                <div className="form-group" style={{marginTop:8}}>
                  <label style={{fontSize:12}}>Raison (optionnel)</label>
                  <input className="form-input" value={punchInNote} onChange={e=>setPunchInNote(e.target.value)} placeholder="ex: réunion préalable au chantier"/>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setTimeModal(null)}>{t.common.cancel}</button>
              <button className="btn btn-primary" onClick={confirmTimeModal}>Confirmer</button>
            </div>
          </div>
        </div>
      )}

      {/* Allocation screen */}
      {allocSession&&(
        <div className="modal-backdrop" onClick={()=>{setAllocSession(null);setEditSession(null);}}>
          <div className="modal" style={{maxWidth:480}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <div className="modal-title"><Icon name="timesheets" size={15}/> {editSession ? tt.resubmit : tt.allocationTitle}</div>
                <div style={{fontSize:12,color:"var(--text-3)",marginTop:2,overflowWrap:"break-word",wordBreak:"break-word"}}>
                  {allocSession.isOverrideRealloc
                    ? `Répartition requise — ${editingTimesheet?.employeeName} · ${toHM(allocTotalHours(allocSession))}`
                    : allocSession.isEdit
                      ? `Modification superviseur — ${editingTimesheet?.employeeName}`
                      : tt.workedToday.replace("{h}",toHM(allocTotalHours(allocSession)))}
                </div>
              </div>
            </div>
            <div className="modal-body alloc-body">
              {(()=>{
                const job=jobs.find(j=>j.id===allocSession.jobId);
                const phases=job?.phases||[];
                const equipOptions=getEquipmentOptions(allocSession.jobId);
                const totalH=allocTotalHours(allocSession);
                const approvedTs=(timesheets||[]).filter(ts=>ts.jobId===allocSession.jobId&&ts.status==="approved");
                const loggedByPhase={};
                approvedTs.forEach(ts=>(ts.taskAllocations||[]).forEach(a=>{if(a.phaseId)loggedByPhase[a.phaseId]=(loggedByPhase[a.phaseId]||0)+(parseFloat(a.hours)||0);}));
                const remaining=Math.round((totalH-allocatedHours)*100)/100;
                return (
                  <>
                    <label style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",background:"var(--bg)",borderRadius:"var(--radius-sm)",marginBottom:14,cursor:"pointer",fontSize:13,border:"1px solid var(--border)"}}>
                      <input type="checkbox" checked={breakTaken} onChange={e=>setBreakTaken(e.target.checked)}/>{tt.breakLunch}
                    </label>
                    <div style={{display:"flex",gap:"4px 12px",flexWrap:"wrap",marginBottom:14,fontSize:13,fontWeight:600,padding:"8px 12px",background:Math.abs(remaining)<0.01?"#f0fdf4":"#fef2f2",borderRadius:"var(--radius-sm)"}}>
                      <span>{tt.allocated}: <strong style={{color:Math.abs(remaining)<0.01?"var(--green)":"var(--red)"}}>{toHM(allocatedHours)}</strong></span>
                      <span style={{color:"var(--text-3)"}}>/ {toHM(totalH)}</span>
                      {Math.abs(remaining)>=0.01&&<span style={{color:"var(--red)"}}>{remaining>0?`${toHM(remaining)} à répartir`:`${toHM(-remaining)} en trop`}</span>}
                    </div>
                    {taskAllocations.map(row=>(
                      <div key={row.id} style={{border:"1px solid var(--border)",borderRadius:"var(--radius-sm)",padding:"12px",marginBottom:10}}>
                        {(()=>{const ph=phases.find(p=>p.id===row.phaseId);const budget=ph?.labourHours||0;const logged=loggedByPhase[row.phaseId]||0;const hint=budget>0?(()=>{const ratio=logged/budget;const color=ratio>=1?"var(--red)":ratio>=0.8?"#d97706":"var(--green)";return <span style={{fontSize:11,color,marginLeft:6,fontWeight:500,whiteSpace:"nowrap"}}>{toHM(logged)} / {toHM(budget)}</span>;})():null;const dec=parseFloat(row.hours)||0;const hhVal=Math.floor(dec);const mmVal=Math.round((dec-hhVal)*60);return(
                        <div style={{display:"flex",gap:8,marginBottom:10,alignItems:"flex-end",width:"100%"}}>
                          <div className="form-group alloc-phase-select" style={{margin:0}}>
                            <label style={{fontSize:11,color:"var(--text-3)",display:"flex",alignItems:"center",flexWrap:"wrap",gap:"2px 4px"}}>{tt.phaseLabel}{hint}</label>
                            <select className="form-select" value={row.phaseId} onChange={e=>{const ph=phases.find(p=>p.id===e.target.value);updateAllocRow(row.id,"phaseId",e.target.value);updateAllocRow(row.id,"phaseName",ph?.title||"Autre");}}>
                              {phases.map(p=><option key={p.id} value={p.id}>{p.title}</option>)}
                              <option value="autre">{tt.phaseOther}</option>
                            </select>
                          </div>
                          <div className="form-group" style={{margin:0}}>
                            <label style={{fontSize:11,color:"var(--text-3)"}}>{tt.hoursLabel}</label>
                            <div style={{display:"flex",gap:4,alignItems:"center"}}>
                              <input type="text" inputMode="numeric" autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false} className="form-input alloc-hm-input" value={hhVal} onChange={e=>{const h=Math.min(24,Math.max(0,parseInt(e.target.value)||0));updateAllocRow(row.id,"hours",h+mmVal/60);}} onFocus={e=>e.target.select()}/>
                              <span style={{fontSize:12,color:"var(--text-3)",flexShrink:0}}>h</span>
                              <input type="text" inputMode="numeric" autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false} className="form-input alloc-hm-input" value={mmVal} onChange={e=>{const raw=e.target.value.replace(/\D/g,"").slice(0,2);const m=Math.min(59,parseInt(raw)||0);updateAllocRow(row.id,"hours",hhVal+m/60);if(raw.length>=2)e.target.blur();}} onFocus={e=>e.target.select()}/>
                              <span style={{fontSize:12,color:"var(--text-3)",flexShrink:0}}>min</span>
                            </div>
                          </div>
                          <button className="btn-icon danger" style={{flexShrink:0,marginBottom:2}} title={t.tooltips.deleteAllocation} onClick={()=>removeAllocRow(row.id)}><Icon name="trash" size={13}/></button>
                        </div>
                        );})()}
                        <div style={{display:"flex",gap:4,marginBottom:row.type==="equipment"?8:0}}>
                          <button className={`btn btn-sm ${row.type==="labour"?"btn-primary":"btn-secondary"}`} onClick={()=>updateAllocRow(row.id,"type","labour")} style={{flex:1,fontSize:12}}>{tt.typeLabour}</button>
                          <button className={`btn btn-sm ${row.type==="equipment"?"btn-primary":"btn-secondary"}`} onClick={()=>updateAllocRow(row.id,"type","equipment")} style={{flex:1,fontSize:12}}>{tt.typeEquipment}</button>
                        </div>
                        {row.type==="equipment"&&(
                          <div className="form-group" style={{margin:0}}>
                            <label style={{fontSize:11,color:"var(--text-3)"}}>{tt.equipmentLabel}</label>
                            <select className="form-select" value={row.equipmentId} onChange={e=>{const eq=equipOptions.find(o=>o.id===e.target.value);updateAllocRow(row.id,"equipmentId",e.target.value);updateAllocRow(row.id,"equipmentName",eq?.name||"");}}>
                              <option value="">{tt.selectEquipment}</option>
                              {equipOptions.map(o=><option key={o.id} value={o.id}>{o.name}</option>)}
                            </select>
                          </div>
                        )}
                      </div>
                    ))}
                    <button className="btn btn-secondary btn-sm" style={{marginBottom:16}} onClick={addAllocRow}><Icon name="plus" size={13}/>{tt.addPhaseRow}</button>
                    <div className="form-group">
                      <label style={{fontSize:12}}>{tt.dayNotes}</label>
                      <textarea className="form-input" rows={3} value={dayNotes} onChange={e=>setDayNotes(e.target.value)} placeholder="Optionnel…" style={{resize:"vertical"}}/>
                    </div>
                  </>
                );
              })()}
            </div>
            <div className="modal-footer alloc-footer">
              <button className="btn btn-secondary" onClick={()=>{setAllocSession(null);setEditSession(null);}}>{t.common.cancel}</button>
              <button className="btn btn-primary" disabled={Math.abs(allocatedHours-allocTotalHours(allocSession))>=0.01||actionPending} onClick={confirmAllocation}>
                <Icon name="clock" size={13}/>{allocSession.isOverrideRealloc ? "Répartir et approuver / Allocate & Approve" : editSession ? tt.resubmit : tt.confirmAllocation}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Override punch-out modal */}
      {overrideModal&&(()=>{
        const ts = overrideModal.ts;
        const punchInMs = new Date(ts.punchInFinal || ts.punchIn).getTime();
        const overrideMs = getOverrideTimeMs();
        const confirmDisabled = !overrideMs || overrideMs >= overrideModal.modalOpenedAt || overrideMs <= punchInMs || actionPending;
        const minStr = fmtTimeInput(punchInMs);
        const maxStr = fmtTimeInput(overrideModal.modalOpenedAt);
        return (
          <div className="modal-backdrop" onClick={()=>setOverrideModal(null)}>
            <div className="modal" style={{maxWidth:420}} onClick={e=>e.stopPropagation()}>
              <div className="modal-header">
                <div className="modal-title"><Icon name="clock" size={15}/> Corriger l'heure de départ / Override Punch-Out</div>
                <button className="btn-icon" title={t.common.close} onClick={()=>setOverrideModal(null)}><Icon name="x"/></button>
              </div>
              <div className="modal-body">
                <div style={{fontSize:13,marginBottom:16,padding:"12px 14px",background:"var(--bg)",borderRadius:"var(--radius-sm)"}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                    <span style={{color:"var(--text-3)"}}>Employé / Employee</span>
                    <strong>{ts.employeeName}</strong>
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between"}}>
                    <span style={{color:"var(--text-3)"}}>Pointé à / Punched in at</span>
                    <strong>{new Date(ts.punchInFinal || ts.punchIn).toLocaleTimeString("fr-CA",{hour:"2-digit",minute:"2-digit"})}</strong>
                  </div>
                </div>
                <div className="form-group">
                  <label style={{fontSize:12}}>Heure de départ / Punch-Out Time</label>
                  <input
                    type="time"
                    className="form-input"
                    value={overrideTime}
                    min={minStr}
                    max={maxStr}
                    onChange={e=>{setOverrideTime(e.target.value);setOverrideError("");}}
                  />
                  <div style={{fontSize:11,color:"var(--text-3)",marginTop:4}}>
                    Min: {new Date(punchInMs).toLocaleTimeString("fr-CA",{hour:"2-digit",minute:"2-digit"})} · Max: {new Date(overrideModal.modalOpenedAt).toLocaleTimeString("fr-CA",{hour:"2-digit",minute:"2-digit"})}
                  </div>
                </div>
                {overrideError&&<div style={{fontSize:12,color:"var(--red)",padding:"8px 10px",background:"#fee2e2",borderRadius:"var(--radius-sm)",marginTop:8}}>{overrideError}</div>}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={()=>setOverrideModal(null)}>{t.common.cancel}</button>
                <button className="btn btn-primary" disabled={confirmDisabled} onClick={confirmOverride}>
                  <Icon name="check" size={13}/> Confirmer
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Edit punch times modal — admin only */}
      {editTimesModal&&(()=>{
        const ts = editTimesModal.ts;
        const refDate = new Date(ts.punchInFinal || ts.punchIn);
        const newInMs = getEditTimesMs(editTimesIn, refDate);
        const newOutMs = getEditTimesMs(editTimesOut, refDate);
        const maxStr = fmtTimeInput(editTimesModal.modalOpenedAt);
        const confirmDisabled = !newInMs || !newOutMs || newInMs >= newOutMs || newInMs >= editTimesModal.modalOpenedAt || newOutMs >= editTimesModal.modalOpenedAt || actionPending;
        return (
          <div className="modal-backdrop" onClick={()=>setEditTimesModal(null)}>
            <div className="modal" style={{maxWidth:440}} onClick={e=>e.stopPropagation()}>
              <div className="modal-header">
                <div className="modal-title"><Icon name="edit" size={15}/> Modifier les heures / Edit Times</div>
                <button className="btn-icon" title={t.common.close} onClick={()=>setEditTimesModal(null)}><Icon name="x"/></button>
              </div>
              <div className="modal-body">
                <div style={{fontSize:13,marginBottom:16,padding:"12px 14px",background:"var(--bg)",borderRadius:"var(--radius-sm)"}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                    <span style={{color:"var(--text-3)"}}>Employé / Employee</span>
                    <strong>{ts.employeeName}</strong>
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between"}}>
                    <span style={{color:"var(--text-3)"}}>Date</span>
                    <strong>{ts.punchIn?new Date(ts.punchIn).toLocaleDateString("fr-CA"):""}</strong>
                  </div>
                </div>
                <div className="form-group">
                  <label style={{fontSize:12}}>Heure d'entrée / Punch-in</label>
                  <input type="time" className="form-input" value={editTimesIn} max={maxStr} onChange={e=>{setEditTimesIn(e.target.value);setEditTimesError("");}}/>
                </div>
                <div className="form-group">
                  <label style={{fontSize:12}}>Heure de départ / Punch-out</label>
                  <input type="time" className="form-input" value={editTimesOut} max={maxStr} onChange={e=>{setEditTimesOut(e.target.value);setEditTimesError("");}}/>
                  <div style={{fontSize:11,color:"var(--text-3)",marginTop:4}}>Max: {new Date(editTimesModal.modalOpenedAt).toLocaleTimeString("fr-CA",{hour:"2-digit",minute:"2-digit"})}</div>
                </div>
                {editTimesError&&<div style={{fontSize:12,color:"var(--red)",padding:"8px 10px",background:"#fee2e2",borderRadius:"var(--radius-sm)",marginTop:4}}>{editTimesError}</div>}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={()=>setEditTimesModal(null)}>{t.common.cancel}</button>
                <button className="btn btn-primary" disabled={confirmDisabled} onClick={confirmEditTimes}>
                  <Icon name="check" size={13}/> Confirmer
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Manual entry modal — admin only */}
      {manualEntryModal&&(()=>{
        const { employeeId, jobId, date, punchIn: piTime, punchOut: poTime, breakTaken: bt, notes } = manualEntryForm;
        const todayStr = new Date().toISOString().slice(0,10);
        const inMs = date && piTime ? new Date(date+"T"+piTime+":00").getTime() : 0;
        const outMs = date && poTime ? new Date(date+"T"+poTime+":00").getTime() : 0;
        const timesValid = !piTime || !poTime || outMs > inMs;
        const canSubmit = employeeId && jobId && date && piTime && poTime && date <= todayStr && timesValid && !manualEntryPending;
        return (
          <div className="modal-backdrop" onClick={()=>setManualEntryModal(false)}>
            <div className="modal" style={{maxWidth:480}} onClick={e=>e.stopPropagation()}>
              <div className="modal-header">
                <div className="modal-title"><Icon name="plus" size={15}/> Nouvelle entrée manuelle / New Manual Entry</div>
                <button className="btn-icon" title={t.common.close} onClick={()=>setManualEntryModal(false)}><Icon name="x"/></button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label style={{fontSize:12}}>Employé / Employee *</label>
                  <select className="form-select" value={employeeId} onChange={e=>setManualEntryForm(f=>({...f,employeeId:e.target.value}))}>
                    <option value="">— Sélectionner —</option>
                    {teamMembers.map(m=><option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label style={{fontSize:12}}>Chantier / Job *</label>
                  <select className="form-select" value={jobId} onChange={e=>setManualEntryForm(f=>({...f,jobId:e.target.value}))}>
                    <option value="">— Sélectionner —</option>
                    {jobs.filter(j=>j.status==="inProgress"||j.status==="pending").map(j=><option key={j.id} value={j.id}>{j.title}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label style={{fontSize:12}}>Date *</label>
                  <input type="date" className="form-input" value={date} max={todayStr} onChange={e=>setManualEntryForm(f=>({...f,date:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label style={{fontSize:12}}>Heure d'entrée / Punch-in *</label>
                  <input type="time" className="form-input" value={piTime} onChange={e=>setManualEntryForm(f=>({...f,punchIn:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label style={{fontSize:12}}>Heure de départ / Punch-out *</label>
                  <input type="time" className="form-input" value={poTime} onChange={e=>setManualEntryForm(f=>({...f,punchOut:e.target.value}))}/>
                </div>
                <label style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",background:"var(--bg)",borderRadius:"var(--radius-sm)",marginBottom:12,cursor:"pointer",fontSize:13,border:"1px solid var(--border)"}}>
                  <input type="checkbox" checked={bt} onChange={e=>setManualEntryForm(f=>({...f,breakTaken:e.target.checked}))}/>{tt.breakLunch}
                </label>
                <div className="form-group">
                  <label style={{fontSize:12}}>{tt.dayNotes}</label>
                  <textarea className="form-input" rows={2} value={notes} onChange={e=>setManualEntryForm(f=>({...f,notes:e.target.value}))} placeholder="ex: Employé absent du système — entrée manuelle" style={{resize:"vertical"}}/>
                </div>
                {!timesValid&&piTime&&poTime&&<div style={{fontSize:12,color:"var(--red)",padding:"8px 10px",background:"#fee2e2",borderRadius:"var(--radius-sm)"}}>{lang==="fr"?"L'heure de départ doit être après l'heure d'entrée":"Punch-out must be after punch-in"}</div>}
                {manualEntryError&&<div style={{fontSize:12,color:"var(--red)",padding:"8px 10px",background:"#fee2e2",borderRadius:"var(--radius-sm)",marginTop:4}}>{manualEntryError}</div>}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={()=>setManualEntryModal(false)}>{t.common.cancel}</button>
                <button className="btn btn-primary" disabled={!canSubmit} onClick={confirmManualEntry}>
                  <Icon name="check" size={13}/> Confirmer
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Reject modal */}
      {rejectModal&&(
        <div className="modal-backdrop" onClick={()=>setRejectModal(null)}>
          <div className="modal" style={{maxWidth:440}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title" style={{color:"var(--red)"}}><Icon name="x" size={15}/> {tt.reject} — {rejectModal.ts.employeeName}</div>
              <button className="btn-icon" title={t.common.close} onClick={()=>setRejectModal(null)}><Icon name="x"/></button>
            </div>
            <div className="modal-body">
              <div style={{fontSize:13,color:"var(--text-2)",marginBottom:12}}>
                {rejectModal.ts.punchIn?new Date(rejectModal.ts.punchIn).toLocaleDateString("fr-CA"):""} · {rejectModal.ts.jobTitle} · {((rejectModal.ts.roundedDuration||0)/3600000).toFixed(2)}h
              </div>
              <div className="form-group">
                <label style={{fontSize:12}}>{tt.rejectReason} *</label>
                <textarea className="form-input" rows={3} value={rejectReason} onChange={e=>setRejectReason(e.target.value)} placeholder={tt.rejectReasonPlaceholder} style={{resize:"vertical"}} autoFocus/>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setRejectModal(null)}>{t.common.cancel}</button>
              <button className="btn btn-danger" disabled={!rejectReason.trim()||actionPending} onClick={confirmReject}>{tt.reject}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Timesheets;
